import type { ColumnFiltersState, RowModel } from '@tanstack/react-table'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
  ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useRef, useState } from 'react'

/** Column ids shared between card filter bar, table columns, and persisted search state / URL. */
const SYNCED_FILTER_IDS = new Set([
  'title',
  'documentTitle',
  'system',
  'drawing',
  'vendor',
  'docType',
])

export function cardFiltersToSyncedColumnFilters(
  cardFilters: CardFilterValues
): ColumnFiltersState {
  return Object.entries(cardFilters)
    .filter(
      ([id, v]) =>
        SYNCED_FILTER_IDS.has(id) &&
        ((typeof v === 'string' && v.length > 0) ||
          (Array.isArray(v) && v.length > 0))
    )
    .map(([id, value]) => ({ id, value }))
}

export function columnFiltersToCardFilters(
  filters: ColumnFiltersState | undefined
): CardFilterValues {
  const out: CardFilterValues = {}
  if (!filters?.length) return out
  for (const { id, value } of filters) {
    if (!SYNCED_FILTER_IDS.has(id)) continue
    if (typeof value === 'string' && value.length > 0) {
      out[id] = value
    } else if (Array.isArray(value) && value.length > 0) {
      out[id] = value as string[]
    }
  }
  return out
}

type OnSearchStateChange = (updates: SearchStateUpdate) => void

export function useDocumentSearchResultFilters({
  searchStateFilters,
  onSearchStateChange,
  viewMode,
}: {
  searchStateFilters: SearchHistoryItem['searchState']['filters']
  onSearchStateChange: OnSearchStateChange
  viewMode: ViewMode
}) {
  const [cardFilters, setCardFilters] = useState<CardFilterValues>(() =>
    columnFiltersToCardFilters(searchStateFilters)
  )
  const cardFiltersRef = useRef(cardFilters)
  cardFiltersRef.current = cardFilters

  const columnFiltersStateKey = JSON.stringify(searchStateFilters ?? [])

  useEffect(() => {
    setCardFilters(columnFiltersToCardFilters(searchStateFilters))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnFiltersStateKey is the canonical content key
  }, [columnFiltersStateKey])

  const commitCardFiltersToUrl = useCallback(() => {
    const synced = cardFiltersToSyncedColumnFilters(cardFiltersRef.current)
    onSearchStateChange({ filters: synced })
  }, [onSearchStateChange])

  const handleTableFilteredDataChange = useCallback(
    (
      _filtered: RowModel<DocumentTableRow>,
      _globalFilter: unknown,
      columnFilters: ColumnFiltersState
    ) => {
      if (viewMode !== 'table') return
      const prev = searchStateFilters ?? []
      if (isEqual(columnFilters, prev)) return
      onSearchStateChange({ filters: columnFilters })
    },
    [viewMode, searchStateFilters, onSearchStateChange]
  )

  return {
    cardFilters,
    setCardFilters,
    commitCardFiltersToUrl,
    handleTableFilteredDataChange,
  }
}
